import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { RootState, AppDispatch } from "../../redux/store";
import { FormBuilder } from "../form/FormBuilder";
import {
  Invoice,
  InvoiceLineItem,
  PaymentCurrency,
  BillingCode,
} from "../../interfaces/screens/billing";
import CurrencyDisplay from "../CurrencyDisplay";
import { fetchPatients } from "../../redux/patients/actions";
import { fetchInsurers } from "../../screens/content/settings/Insurers/actions";
import { fetchCurrencies } from "../../screens/content/settings/Currencies/actions";
import { fetchBillingCodes } from "../../screens/content/billing/actions";
import { FormComponent } from "../../shared/utils/enums";
import { Trash2, Plus } from "lucide-react";
import MainTable from "../mainTable";
import { TableColumnsItem } from "../../interfaces/components/mainTable";
import { ColumnType } from "../../shared/utils/enums";
import { Select, SelectOption } from "../Select";
import { getAmountInCurrency } from "../../shared/utils/currency";

interface InvoiceEditorProps {
  invoice?: Invoice;
  onClose: () => void;
  patientId?: string;
}

type FormDataType = Partial<Invoice & { currency: string }>;

const InvoiceEditor: React.FC<InvoiceEditorProps> = ({
  invoice,
  onClose,
  patientId,
}) => {
  const dispatch: AppDispatch = useDispatch();
  const { data: patients } = useSelector((state: RootState) => state.patients);
  const { data: insurers } = useSelector((state: RootState) => state.insurers);
  const { data: currencies } = useSelector(
    (state: RootState) => state.currencies,
  );
  const { data: billingCodes } = useSelector(
    (state: RootState) => state.billing.billingCodes,
  );
  const defaultCurrency =
    currencies.find((c) => c.is_default)?.code || "USD";

  const [formData, setFormData] = useState<FormDataType>(() => {
    const initialCurrency =
      invoice?.line_items?.[0]?.currency || defaultCurrency;
    return (
      invoice || {
        status: "draft",
        is_insured: false,
        requires_claim_submission: false,
        patient_id: patientId,
      }
    );
  });


  const [lineItems, setLineItems] = useState<
    Partial<InvoiceLineItem & { base_unit_price?: number }>[]
  >(
    invoice?.line_items.map((item) => ({
      ...item,
      base_unit_price:
        billingCodes.find(
          (c) => c.code === item.cpt_code || c.code === item.icd_code,
        )?.unit_price || item.unit_price,
    })) || [],
  );

  const handleFieldChange = useCallback((field: string, value: any) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  }, []);

  useEffect(() => {
    if (patientId && !formData.patient_id) {
      handleFieldChange("patient_id", patientId);
    }
  }, [patientId, formData.patient_id, handleFieldChange]);
  
  const selectedCurrency = formData.currency || defaultCurrency;

  useEffect(() => {
    // For new invoices, if the default currency changes, update the selected currency.
    if (!invoice) {
      handleFieldChange("currency", defaultCurrency);
    }
  }, [defaultCurrency, invoice, handleFieldChange]);

  useEffect(() => {
    if (patients.length === 0) {
      dispatch(fetchPatients({ page: 1, pageSize: 100 }));
    }
    if (insurers.length === 0) {
      dispatch(fetchInsurers());
    }
    if (currencies.length === 0) {
      dispatch(fetchCurrencies());
    }
    if (billingCodes.length === 0) {
      dispatch(fetchBillingCodes());
    }
  }, [
    dispatch,
    patients.length,
    insurers.length,
    currencies.length,
    billingCodes.length,
  ]);

  useEffect(() => {
    const newCurrencyInfo = currencies.find((c) => c.code === selectedCurrency);
    if (!newCurrencyInfo) return;

    setLineItems((prevLineItems) =>
      prevLineItems.map((item) => {
        const unitPrice = item.base_unit_price
          ? getAmountInCurrency(item.base_unit_price, newCurrencyInfo)
          : item.unit_price;

        return {
          ...item,
          currency: selectedCurrency as PaymentCurrency,
          unit_price: unitPrice,
        };
      }),
    );
  }, [selectedCurrency, currencies]);

  const handleLineItemChange = useCallback(
    (index: number, field: keyof InvoiceLineItem, value: any) => {
      const updatedLineItems = lineItems.map((item, i) => {
        if (i === index) {
          const newItem = { ...item, [field]: value };
          if (field === "unit_price") {
            newItem.base_unit_price = undefined;
          }
          return newItem;
        }
        return item;
      });
      setLineItems(updatedLineItems);
    },
    [lineItems],
  );

  const handleCodeSelection = useCallback(
    (index: number, codeId: string | null) => {
      if (!codeId) {
        const updatedLineItems = lineItems.map((item, i) => {
          if (i === index) {
            return {
              ...item,
              description: "",
              unit_price: 0,
              cpt_code: undefined,
              icd_code: undefined,
              currency: selectedCurrency as PaymentCurrency,
            };
          }
          return item;
        });
        setLineItems(updatedLineItems);
        return;
      }

      const selectedCode = billingCodes.find((c) => c.id === codeId);
      if (selectedCode) {
        const currencyInfo = currencies.find(
          (c) => c.code === selectedCurrency,
        );
        const unitPrice = getAmountInCurrency(
          selectedCode.unit_price || 0,
          currencyInfo,
        );

        const updatedLineItems = lineItems.map((item, i) => {
          if (i === index) {
            const updatedItem = {
              ...item,
              description: selectedCode.description,
              unit_price: unitPrice,
              base_unit_price: selectedCode.unit_price || 0,
              currency: selectedCurrency as PaymentCurrency,
            };
            if (selectedCode.type === "cpt") {
              updatedItem.cpt_code = selectedCode.code;
            } else if (selectedCode.type === "icd") {
              updatedItem.icd_code = selectedCode.code;
            }
            return updatedItem;
          }
          return item;
        });
        setLineItems(updatedLineItems);
      }
    },
    [lineItems, billingCodes, currencies, selectedCurrency],
  );

  const handleRemoveLineItem = useCallback(
    (index: number) => {
      const newLineItems = lineItems.filter((_, i) => i !== index);
      setLineItems(newLineItems);
    },
    [lineItems],
  );

  const addLineItem = useCallback(() => {
    setLineItems([
      ...lineItems,
      {
        description: "",
        quantity: 1,
        unit_price: 0,
        currency: selectedCurrency as PaymentCurrency,
        nssf_coverage_percentage: 80,
        moph_coverage_percentage: 100,
        requires_preauthorization: false,
      },
    ]);
  }, [lineItems, selectedCurrency]);

  const handleFormSubmit = useCallback(
    (submittedData: any) => {
      const finalInvoice = {
        ...invoice,
        ...submittedData,
        line_items: lineItems,
      };
      console.log("Submitting Invoice:", finalInvoice);
      onClose();
    },
    [invoice, lineItems, onClose],
  );

  const invoiceFormSchema: any = useMemo(() => {
    const baseProperties = {
      issue_date: {
        label: "Issue Date",
        component: FormComponent.DatePicker,
        type: "string",
        required: true,
        componentProps: {
          value: formData.issue_date,
          onChange: (v: any) => handleFieldChange("issue_date", v),
        },
      },
      due_date: {
        label: "Due Date",
        component: FormComponent.DatePicker,
        type: "string",
        required: true,
        componentProps: {
          value: formData.due_date,
          onChange: (v: any) => handleFieldChange("due_date", v),
        },
      },
      currency: {
        label: "Currency",
        component: FormComponent.Select,
        type: "string",
        required: true,
        options: currencies.map((c) => ({
          value: c.code,
          label: `${c.name} (${c.code})`,
        })),
        componentProps: {
          value: formData.currency || defaultCurrency,
          onChange: (v: any) => handleFieldChange("currency", v),
        },
      },
      insurer_id: {
        label: "Insurer",
        component: FormComponent.Select,
        type: "string",
        required: false,
        options:
          insurers.map((i) => ({ value: i.id, label: i.name })) || [],
        componentProps: {
          value: formData.insurer_id,
          onChange: (v: any) => handleFieldChange("insurer_id", v),
        },
      },
      status: {
        label: "Status",
        component: FormComponent.Select,
        type: "string",
        required: true,
        options: [
          { value: "draft", label: "Draft" },
          { value: "issued", label: "Issued" },
          { value: "paid", label: "Paid" },
          { value: "partially_paid", label: "Partially Paid" },
          { value: "pending_insurance", label: "Pending Insurance" },
        ],
        componentProps: {
          value: formData.status,
          onChange: (v: any) => handleFieldChange("status", v),
        },
      },
      insurance_type: {
        label: "Insurance Type",
        component: FormComponent.Select,
        type: "string",
        required: false,
        options: [
          { value: "nssf", label: "NSSF" },
          { value: "moph", label: "MOPH" },
          { value: "private", label: "Private Insurance" },
          { value: "cooperative", label: "Cooperative Insurance" },
          { value: "corporate", label: "Corporate Insurance" },
        ],
        componentProps: {
          value: formData.insurance_type,
          onChange: (v: any) => handleFieldChange("insurance_type", v),
        },
      },
      claim_deadline: {
        label: "Claim Deadline",
        component: FormComponent.DatePicker,
        type: "string",
        required: false,
        componentProps: {
          value: formData.claim_deadline,
          onChange: (v: any) => handleFieldChange("claim_deadline", v),
        },
      },
      notes_internal: {
        label: "Internal Notes",
        component: FormComponent.Textarea,
        type: "string",
        required: false,
        componentProps: {
          value: formData.notes_internal,
          onChange: (v: any) => handleFieldChange("notes_internal", v),
          rows: 3,
        },
      },
      is_insured: {
        label: "Is Insured",
        component: FormComponent.Checkbox,
        type: "boolean",
        required: false,
        componentProps: {
          checked: formData.is_insured,
          onChange: (v: any) => handleFieldChange("is_insured", v),
        },
      },
      requires_claim_submission: {
        label: "Requires Claim Submission",
        component: FormComponent.Checkbox,
        type: "boolean",
        required: false,
        componentProps: {
          checked: formData.requires_claim_submission,
          onChange: (v: any) =>
            handleFieldChange("requires_claim_submission", v),
        },
      },
    };

    const patientProperty = !patientId
      ? {
          patient_id: {
            label: "Patient",
            component: FormComponent.Select,
            type: "string",
            required: true,
            options:
              patients.map((p: any) => ({
                value: String(p.id),
                label: p.full_name,
              })) || [],
            componentProps: {
              value: formData.patient_id,
              onChange: (v: any) => handleFieldChange("patient_id", v),
            },
          },
        }
      : {};

    return {
      type: "object",
      properties: {
        ...patientProperty,
        ...baseProperties,
      },
    };
  }, [
    patientId,
    patients,
    insurers,
    currencies,
    formData,
    handleFieldChange,
  ]);

  const totalAmount = lineItems.reduce((acc, item) => {
    const price = Number(item.unit_price) || 0;
    const quantity = Number(item.quantity) || 0;
    return acc + price * quantity;
  }, 0);

  const inputClasses =
    "w-full px-2 py-1.5 text-sm rounded-lg focus:outline-none focus:ring-2 bg-transparent focus:ring-blue-500 dark:text-white border border-gray-300 dark:border-gray-700";

  const lineItemColumns: TableColumnsItem[] = useMemo(
    () => [
      {
        key: "description",
        name: "Description",
        type: ColumnType.CUSTOM,
        width: "300px",
        customValue: (row: InvoiceLineItem, index: number) => {
          // Find the billing code that matches this description
          const selectedCode = billingCodes.find(
            (code) => code.description === row.description,
          );

          const selectOptions: SelectOption[] = billingCodes.map(
            (code: BillingCode) => ({
              value: code.id,
              label: `${code.code} - ${code.description}`,
            }),
          );

          return (
            <div className="w-[300px]">
              <Select
                placeholder="Select a service or item"
                value={selectedCode?.id}
                onChange={(value) => handleCodeSelection(index, value)}
                options={selectOptions}
                showSearch={true}
                allowClear={true}
                dropdownWidth={400}
              />
            </div>
          );
        },
      },
      {
        key: "quantity",
        name: "Quantity",
        type: ColumnType.CUSTOM,
        width: "120px",
        customValue: (row: InvoiceLineItem, index: number) => (
          <input
            type="number"
            value={String(row.quantity || "0")}
            className={`${inputClasses} w-full text-right`}
            onChange={(e) =>
              handleLineItemChange(index, "quantity", Number(e.target.value))
            }
          />
        ),
      },
      {
        key: "unit_price",
        name: "Unit Price",
        type: ColumnType.CUSTOM,
        width: "150px",
        customValue: (row: InvoiceLineItem, index: number) => (
          <input
            type="number"
            value={String(row.unit_price || "0")}
            className={`${inputClasses} w-full text-right`}
            onChange={(e) =>
              handleLineItemChange(index, "unit_price", Number(e.target.value))
            }
          />
        ),
      },
      {
        key: "nssf_coverage_percentage",
        name: "NSSF %",
        type: ColumnType.CUSTOM,
        width: "100px",
        customValue: (row: InvoiceLineItem, index: number) => (
          <input
            type="number"
            value={String(row.nssf_coverage_percentage || "0")}
            className={`${inputClasses} w-full text-right`}
            onChange={(e) =>
              handleLineItemChange(
                index,
                "nssf_coverage_percentage",
                Number(e.target.value),
              )
            }
            min="0"
            max="100"
          />
        ),
      },
      {
        key: "moph_coverage_percentage",
        name: "MOPH %",
        type: ColumnType.CUSTOM,
        width: "100px",
        customValue: (row: InvoiceLineItem, index: number) => (
          <input
            type="number"
            value={String(row.moph_coverage_percentage || "0")}
            className={`${inputClasses} w-full text-right`}
            onChange={(e) =>
              handleLineItemChange(
                index,
                "moph_coverage_percentage",
                Number(e.target.value),
              )
            }
            min="0"
            max="100"
          />
        ),
      },
      {
        key: "requires_preauthorization",
        name: "Pre-Auth",
        type: ColumnType.CUSTOM,
        width: "80px",
        customValue: (row: InvoiceLineItem, index: number) => (
          <input
            type="checkbox"
            checked={row.requires_preauthorization || false}
            className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 dark:focus:ring-blue-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
            onChange={(e) =>
              handleLineItemChange(
                index,
                "requires_preauthorization",
                e.target.checked,
              )
            }
          />
        ),
      },
      {
        key: "preauthorization_number",
        name: "Pre-Auth #",
        type: ColumnType.CUSTOM,
        width: "120px",
        customValue: (row: InvoiceLineItem, index: number) => (
          <input
            type="text"
            value={row.preauthorization_number || ""}
            className={`${inputClasses} w-full text-center`}
            onChange={(e) =>
              handleLineItemChange(
                index,
                "preauthorization_number",
                e.target.value,
              )
            }
            placeholder="Pre-Auth #"
            disabled={!row.requires_preauthorization}
          />
        ),
      },
      {
        key: "total",
        name: "Total",
        type: ColumnType.CUSTOM,
        width: "150px",
        customValue: (row: InvoiceLineItem) => {
          const quantity = Number(row.quantity) || 0;
          const unitPrice = Number(row.unit_price) || 0;
          const lineTotal = quantity * unitPrice;
          return (
            <CurrencyDisplay
              amount={lineTotal}
              currency={row.currency as PaymentCurrency}
            />
          );
        },
      },
      {
        key: "actions",
        name: " ",
        type: ColumnType.CUSTOM,
        width: "80px",
        customValue: (row: InvoiceLineItem, index: number) => (
          <button
            onClick={() => handleRemoveLineItem(index)}
            className="text-gray-400 hover:text-red-500 p-2 rounded-full transition-colors"
          >
            <Trash2 size={18} />
          </button>
        ),
      },
    ],
    [
      billingCodes,
      handleCodeSelection,
      handleLineItemChange,
      handleRemoveLineItem,
      inputClasses,
    ],
  );

  return (
    <div className="py-4">
      <FormBuilder
        schema={invoiceFormSchema}
        onSubmit={handleFormSubmit}
        initialValues={formData}
        buttonType="cancel-save"
      />

      <section className="mt-8">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Line Items</h3>

        <MainTable
          data={lineItems}
          columns={lineItemColumns}
          showAddButton={false}
          showEmptyList={false}
          showPagination={false}
        />

        <div className="mt-4">
          <button
            onClick={addLineItem}
            className="flex items-center gap-2 px-4 py-2 text-sm bg-white border-2 border-primary text-primary hover:bg-primary hover:text-white font-medium rounded-md transition-colors duration-200 dark:bg-transparent dark:border-primary dark:text-primary dark:hover:bg-primary dark:hover:text-white"
          >
            <Plus size={16} />
            Add Line Item
          </button>
        </div>
      </section>

      <div className="mt-6 text-xl font-bold text-right">
        Total:{" "}
        <CurrencyDisplay
          amount={totalAmount}
          currency={selectedCurrency as PaymentCurrency}
        />
      </div>
    </div>
  );
};

export default InvoiceEditor;